import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, Check, ArrowLeft } from 'lucide-react';

// Copy for each gated module. Keyed by a short module id.
export const MODULE_INFO = {
  'project-hub': {
    title: 'Project Hub',
    tagline: 'Contractors, bid requests, documents & reviews in one command center.',
    bullets: ['Bid request cascades to multiple contractors', 'Contractor W-9 / COI compliance tracking', 'Central document vault & review queue'],
  },
  schedule: {
    title: 'Schedule',
    tagline: 'A living construction calendar across every funded project.',
    bullets: ['Multi-project Gantt & milestone tracking', 'Draw-to-schedule alignment', 'Delay & critical-path flags'],
  },
  reports: {
    title: 'Reports',
    tagline: 'Board-ready draw reports and portfolio exports on demand.',
    bullets: ['Branded PDF draw summaries', 'Portfolio & lender exports', 'Historical draw analytics'],
  },
  'analysis-suite': {
    title: 'Financial Analysis',
    tagline: 'Underwrite deals and run full project financial analysis.',
    bullets: ['Deal underwriting workspace', 'Sensitivity & return modeling', 'All-in cost & LTC/LTV analysis'],
  },
  timeline: {
    title: 'Timeline',
    tagline: 'Visual project timeline with phase-by-phase progress.',
    bullets: ['Phase milestones & progress bars', 'Contingency & delay tracking', 'Shareable timeline view'],
  },
  'spec-sheets': {
    title: 'Spec Sheets',
    tagline: 'Organize materials & specifications by category.',
    bullets: ['Category-level material specs', 'Attach cut sheets & submittals', 'Keep the build to spec'],
  },
  paint: {
    title: 'Paint Colors',
    tagline: 'Track every paint selection in one place.',
    bullets: ['Room-by-room color schedule', 'Brand, code & finish capture', 'Share selections with the crew'],
  },
  'contractor-portal': {
    title: 'Contractor Portal',
    tagline: 'Give contractors their own branded portal.',
    bullets: ['Self-service bid submission', 'Compliance document uploads', 'Two-way messaging'],
  },
};

// Upsell screen shown when a user without the add-on opens a gated module.
// `moduleId` selects the copy; `inline` drops the outer min-h/background so it
// can be embedded inside an existing page shell (e.g. a project tab body).
export default function AddOnUpgrade({ moduleId, inline = false }) {
  const navigate = useNavigate();
  const info = MODULE_INFO[moduleId] || { title: 'This module', tagline: 'Available as a DrawReady add-on.', bullets: [] };

  const card = (
    <div className="max-w-xl w-full mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid={`addon-gate-${moduleId}`}>
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-8 py-7 text-white">
        <div className="flex items-center gap-2 text-blue-100 text-xs font-bold uppercase tracking-wider mb-3">
          <Lock className="w-3.5 h-3.5" /> Premium add-on
        </div>
        <h1 className="text-2xl font-extrabold flex items-center gap-2">
          <Sparkles className="w-6 h-6" /> {info.title}
        </h1>
        <p className="text-blue-100 mt-1.5 text-sm">{info.tagline}</p>
      </div>
      <div className="px-8 py-6">
        <p className="text-slate-600 text-sm mb-4">
          {info.title} isn&apos;t part of your current plan. Add it on to keep your core DrawReady workflow lean while unlocking deeper project tooling when you need it.
        </p>
        {info.bullets.length > 0 && (
          <ul className="space-y-2.5 mb-6">
            {info.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/add-ons')}
            data-testid={`addon-upgrade-btn-${moduleId}`}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Unlock {info.title}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid={`addon-back-btn-${moduleId}`}
            className="inline-flex items-center justify-center gap-2 text-slate-600 hover:text-slate-900 font-medium px-5 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  if (inline) {
    return <div className="py-10 px-4 flex justify-center">{card}</div>;
  }
  return <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">{card}</div>;
}
